import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  FlashOn as MacroIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import ticketMacrosService from '../../services/ticketMacros.service';
import './TicketStyles.css';

interface MacroAction {
  type: string;
  value?: string;
}

interface TicketMacro {
  _id: string;
  name: string;
  description?: string;
  actions: MacroAction[];
  createdAt?: string;
}

const ACTION_TYPES: { value: string; label: string }[] = [
  { value: 'set_status', label: 'تغییر وضعیت' },
  { value: 'set_priority', label: 'تغییر اولویت' },
  { value: 'add_tag', label: 'افزودن تگ' },
  { value: 'remove_tag', label: 'حذف تگ' },
  { value: 'assign_admin', label: 'تخصیص به ادمین' },
  { value: 'add_internal_note', label: 'افزودن یادداشت داخلی' },
  { value: 'reply_message', label: 'ارسال پاسخ' }
];

const TicketMacrosPage: React.FC = () => {
  const [items, setItems] = useState<TicketMacro[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TicketMacro | null>(null);
  const [executeTarget, setExecuteTarget] = useState<TicketMacro | null>(null);
  const [ticketId, setTicketId] = useState('');
  const [form, setForm] = useState<{ name: string; description: string; actions: MacroAction[] }>({
    name: '',
    description: '',
    actions: []
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await ticketMacrosService.list();
      setItems(data);
    } catch (error) {
      console.error('Error loading macros:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onAdd = () => {
    setEditing(null);
    setForm({ name: '', description: '', actions: [] });
    setDialogOpen(true);
  };

  const onEdit = (item: TicketMacro) => {
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description || '',
      actions: item.actions || []
    });
    setDialogOpen(true);
  };

  const onDelete = async (id: string) => {
    if (!window.confirm('آیا از حذف این ماکرو اطمینان دارید؟')) return;
    try {
      await ticketMacrosService.remove(id);
      await load();
    } catch (error) {
      console.error('Error deleting macro:', error);
    }
  };

  const addAction = () => {
    setForm({
      ...form,
      actions: [...form.actions, { type: 'set_status', value: '' }]
    });
  };

  const updateAction = (idx: number, patch: Partial<MacroAction>) => {
    const copy = [...form.actions];
    copy[idx] = { ...copy[idx], ...patch };
    setForm({ ...form, actions: copy });
  };

  const removeAction = (idx: number) => {
    setForm({ ...form, actions: form.actions.filter((_, i) => i !== idx) });
  };

  const onSubmit = async () => {
    try {
      if (editing) {
        await ticketMacrosService.update(editing._id, form);
      } else {
        await ticketMacrosService.create(form);
      }
      setDialogOpen(false);
      await load();
    } catch (error) {
      console.error('Error saving macro:', error);
    }
  };

  const openExecuteDialog = (macro: TicketMacro) => {
    setExecuteTarget(macro);
    setTicketId('');
    setExecuteDialogOpen(true);
  };

  const onExecute = async () => {
    if (!executeTarget || !ticketId.trim()) return;
    try {
      await ticketMacrosService.execute(executeTarget._id, ticketId);
      setExecuteDialogOpen(false);
      alert('ماکرو با موفقیت اجرا شد');
    } catch (error) {
      console.error('Error executing macro:', error);
      alert('خطا در اجرای ماکرو');
    }
  };

  const getActionLabel = (type: string) => {
    return ACTION_TYPES.find(a => a.value === type)?.label || type;
  };

  return (
    <>
      <Helmet>
        <title>ماکروهای تیکت</title>
      </Helmet>

      <div className="ticket-page">
        {/* Header */}
        <div className="ticket-header">
          <div className="ticket-header-title">
            <h1>
              <MacroIcon />
              ماکروهای تیکت
            </h1>
            <span className="ticket-header-subtitle">
              اتوماسیون عملیات تکراری برای مدیریت سریع‌تر تیکت‌ها
            </span>
          </div>

          <div className="header-actions">
            <button className="header-btn" onClick={load} disabled={loading}>
              <RefreshIcon style={{ fontSize: 18 }} />
              بروزرسانی
            </button>
            <button className="header-btn primary" onClick={onAdd}>
              <AddIcon style={{ fontSize: 18 }} />
              ماکرو جدید
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <CircularProgress size={40} />
            <p>در حال بارگذاری...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <MacroIcon />
            <h3>ماکرویی یافت نشد</h3>
            <p>برای شروع یک ماکرو جدید ایجاد کنید</p>
          </div>
        ) : (
          <div className="cards-grid">
            {items.map((item) => (
              <div key={item._id} className="item-card">
                <div className="item-card-header">
                  <h3 className="item-card-title">{item.name}</h3>
                  <div className="item-card-actions">
                    <button
                      className="row-action-btn"
                      onClick={() => openExecuteDialog(item)}
                      title="اجرا"
                    >
                      <PlayIcon style={{ fontSize: 18 }} />
                    </button>
                    <button
                      className="row-action-btn"
                      onClick={() => onEdit(item)}
                      title="ویرایش"
                    >
                      <EditIcon style={{ fontSize: 18 }} />
                    </button>
                    <button
                      className="row-action-btn danger"
                      onClick={() => onDelete(item._id)}
                      title="حذف"
                    >
                      <DeleteIcon style={{ fontSize: 18 }} />
                    </button>
                  </div>
                </div>

                {item.description && (
                  <p className="item-card-content">{item.description}</p>
                )}

                <div className="item-card-meta" style={{ flexDirection: 'column', gap: 8 }}>
                  <span style={{ fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                    <SettingsIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }} />
                    اکشن‌ها ({item.actions?.length || 0})
                  </span>
                  {item.actions?.map((action, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: 'var(--color-bg-default)',
                        padding: '6px 10px',
                        borderRadius: 6,
                        fontSize: 12
                      }}
                    >
                      <span style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                        {getActionLabel(action.type)}
                      </span>
                      {action.value && (
                        <span style={{ color: 'var(--color-text-secondary)', marginRight: 8 }}>
                          → {action.value}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        {dialogOpen && (
          <div className="dialog-overlay" onClick={() => setDialogOpen(false)}>
            <div className="dialog large" onClick={(e) => e.stopPropagation()}>
              <div className="dialog-header">
                <h2>
                  <MacroIcon />
                  {editing ? 'ویرایش ماکرو' : 'ایجاد ماکرو جدید'}
                </h2>
                <button className="dialog-close" onClick={() => setDialogOpen(false)}>
                  <CloseIcon />
                </button>
              </div>

              <div className="dialog-content">
                <div className="form-group">
                  <label className="form-label">نام ماکرو</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="نام ماکرو را وارد کنید"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">توضیحات (اختیاری)</label>
                  <textarea
                    className="form-textarea"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="توضیحات مختصر درباره این ماکرو"
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">اکشن‌ها</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                    {form.actions.map((action, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'center',
                          background: 'var(--color-bg-default)',
                          padding: 12,
                          borderRadius: 10
                        }}
                      >
                        <select
                          className="form-select"
                          value={action.type}
                          onChange={(e) => updateAction(idx, { type: e.target.value })}
                          style={{ flex: 1 }}
                        >
                          {ACTION_TYPES.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          className="form-input"
                          value={action.value || ''}
                          onChange={(e) => updateAction(idx, { value: e.target.value })}
                          placeholder="مقدار"
                          style={{ flex: 2 }}
                        />
                        <button
                          className="row-action-btn danger"
                          onClick={() => removeAction(idx)}
                        >
                          <DeleteIcon style={{ fontSize: 18 }} />
                        </button>
                      </div>
                    ))}

                    <button
                      className="header-btn"
                      onClick={addAction}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      <AddIcon style={{ fontSize: 18 }} />
                      افزودن اکشن
                    </button>
                  </div>
                </div>
              </div>

              <div className="dialog-actions">
                <button className="filter-btn secondary" onClick={() => setDialogOpen(false)}>
                  انصراف
                </button>
                <button
                  className="filter-btn primary"
                  onClick={onSubmit}
                  disabled={!form.name.trim()}
                >
                  ذخیره
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Execute Dialog */}
        {executeDialogOpen && executeTarget && (
          <div className="dialog-overlay" onClick={() => setExecuteDialogOpen(false)}>
            <div className="dialog" onClick={(e) => e.stopPropagation()}>
              <div className="dialog-header">
                <h2>
                  <PlayIcon />
                  اجرای ماکرو
                </h2>
                <button className="dialog-close" onClick={() => setExecuteDialogOpen(false)}>
                  <CloseIcon />
                </button>
              </div>

              <div className="dialog-content">
                <p style={{ marginBottom: 16, color: 'var(--color-text-secondary)' }}>
                  ماکرو <strong style={{ color: 'var(--color-text-primary)' }}>{executeTarget.name}</strong> را روی تیکت مورد نظر اجرا کنید
                </p>
                <div className="form-group">
                  <label className="form-label">شناسه تیکت</label>
                  <input
                    type="text"
                    className="form-input"
                    value={ticketId}
                    onChange={(e) => setTicketId(e.target.value)}
                    placeholder="شناسه تیکت را وارد کنید"
                  />
                </div>
              </div>

              <div className="dialog-actions">
                <button className="filter-btn secondary" onClick={() => setExecuteDialogOpen(false)}>
                  انصراف
                </button>
                <button
                  className="filter-btn primary"
                  onClick={onExecute}
                  disabled={!ticketId.trim()}
                >
                  اجرا
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TicketMacrosPage;
